import React, { useState, useCallback } from "react";
import {
  Layout,
  ConfigProvider,
  Typography,
  Spin,
  Empty,
  Tabs,
  Input,
  Select,
  Divider,
  Card,
  Button,
  List,
  Space,
  App as AntApp,
  Drawer,
} from "antd";
import {
  PlusOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { reviTheme } from "../../../../theme/reviTheme";
import { useShellEstrecho } from "../../../../hooks/useIsNarrow";
import { useAuth } from "../../../../hooks";

import { useEvaluacionesController } from "./useEvaluacionesController";

import { EvaluationItemCard } from "../../../../components/Admin/uiComponents/EvaluationItemCard/EvaluationItemCard";
import { EvaluationDetailModal } from "../../../../components/Admin/EvaluationsCmp/EvaluationDetailModal/EvaluationDetailModal";
import { EvaluationWizard } from "../../../../components/Admin/EvaluationsCmp/EvaluationWizard/EvaluationWizard";
import { SharedEvaluationsModal } from "../../../../components/Admin/EvaluationsCmp/SharedEvaluationsModal/SharedEvaluationsModal";
import { QuickEvaluationModal } from "../../../../components/revi/QuickEvaluationModal";

import "./Page_Therapist_Evaluaciones.scss";

const { Content, Sider } = Layout;
const { Title, Text } = Typography;

function PageTherapistEvaluacionesInner() {
  const { t } = useTranslation("therapist");
  const shellEstrecho = useShellEstrecho();
  const [panelAbierto, setPanelAbierto] = useState(false);
  const { accessToken } = useAuth();
  const { message: messageApi } = AntApp.useApp();

  const {
    loading,
    tab,
    setTab,
    area,
    setArea,
    search,
    setSearch,

    standard,
    myEvals,

    selected, // IMPORTANT: pásalo desde el controller
    detailOpen,
    detailLoading, // optional
    openDetail,
    closeDetail,

    wizardState,
    openCreate,
    openEdit,
    closeWizard,
    onWizardSuccess,

    onDelete,

    sharedOpen,
    sharedLoading,
    sharedEvals,
    openShared,
    closeShared,
    onClone,
  } = useEvaluacionesController();

  const items = tab === "standard" ? standard : myEvals;

  const [quickEvalState, setQuickEvalState] = useState({
    open: false,
    evaluation: null,
  });

  const openQuickEval = useCallback((ev) => {
    setQuickEvalState({ open: true, evaluation: ev });
  }, []);

  const closeQuickEval = useCallback(() => {
    setQuickEvalState({ open: false, evaluation: null });
  }, []);

  return (
    <ConfigProvider theme={reviTheme}>
      <Layout className="revi-page-evaluaciones">
        <Content className="revi-page-evaluaciones__main">
          <div className="revi-page-evaluaciones__header">
            <div>
              <Title level={2} style={{ marginBottom: 0 }}>
                {t("evaluaciones.titulo")}
              </Title>
              <Text type="secondary" className="revi-subtitulo-pagina">
                {t("evaluaciones.subtitulo")}
              </Text>
            </div>

            {/* En estrecho, el unico acceso a crear y a las compartidas. */}
            {shellEstrecho && (
              <Button
                className="revi-page-evaluaciones__panelBtn"
                icon={<UnorderedListOutlined />}
                onClick={() => setPanelAbierto(true)}
                aria-label={t("evaluaciones.acciones.crear")}
              />
            )}
          </div>

          <Card className="revi-page-evaluaciones__toolbar" bordered>
            <Space
              wrap
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Space wrap>
                <Input.Search
                  placeholder={t("evaluaciones.buscarPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  allowClear
                  style={{ width: 320 }}
                />

                <Select
                  placeholder={t("evaluaciones.areaPlaceholder")}
                  value={area}
                  onChange={setArea}
                  allowClear
                  style={{ width: 220 }}
                  // El `value` NO se traduce: es el filtro que viaja a la API y
                  // se compara con el area guardada en la evaluacion. Solo
                  // cambia de idioma la etiqueta que se lee en pantalla.
                  options={[
                    {
                      value: "Cognitiva",
                      label: t("evaluaciones.areas.cognitiva"),
                    },
                    {
                      value: "Emocional",
                      label: t("evaluaciones.areas.emocional"),
                    },
                    {
                      value: "Funcional",
                      label: t("evaluaciones.areas.funcional"),
                    },
                    { value: "Física", label: t("evaluaciones.areas.fisica") },
                    {
                      value: "General",
                      label: t("evaluaciones.areas.general"),
                    },
                  ]}
                />
              </Space>
            </Space>
          </Card>

          {/*
            El recuento va dentro de la plantilla. En frances la etiqueta
            concuerda en femenino plural ("Standardisées"), asi que no se puede
            pegar el parentesis por fuera.
          */}
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              {
                key: "standard",
                label: t("evaluaciones.tabs.estandar", {
                  count: standard.length,
                }),
              },
              {
                key: "my",
                label: t("evaluaciones.tabs.mias", { count: myEvals.length }),
              },
            ]}
          />

          <div className="revi-page-evaluaciones__grid">
            {loading ? (
              <div className="revi-center">
                <Spin size="large" />
              </div>
            ) : items.length === 0 ? (
              <div className="revi-center">
                <Empty description={t("evaluaciones.vacio")} />
              </div>
            ) : (
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4 }}
                dataSource={items}
                renderItem={(ev) => (
                  <List.Item>
                    <EvaluationItemCard
                      evaluation={ev}
                      mode={tab === "my" ? "my" : "standard"}
                      onView={() => openDetail(ev)}
                      onEdit={() => openEdit(ev)}
                      onDelete={() => onDelete(ev)}
                      onQuickApply={openQuickEval}
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        </Content>

        {/*
          Segundo panel lateral dentro del que ya pone el shell: 330 px de
          cromo sobre un viewport de 375. Por debajo de 992 no se renderiza y
          su contenido se monta en un cajon propio.
        */}
        {!shellEstrecho && (
          <Sider
            width={330}
            theme="light"
            className="revi-page-evaluaciones__sider"
          >
            <div className="revi-page-evaluaciones__sider-inner">
              <Card bordered>
                <Space
                  orientation="vertical"
                  style={{ width: "100%" }}
                  size={10}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block
                    onClick={openCreate}
                  >
                    {t("evaluaciones.acciones.crear")}
                  </Button>

                  <Button icon={<TeamOutlined />} block onClick={openShared}>
                    {t("evaluaciones.acciones.compartidas")}
                  </Button>

                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("evaluaciones.leyendaVisibilidad")}
                  </Text>
                </Space>
              </Card>

              <Divider />

              <Card bordered>
                <Text type="secondary">{t("evaluaciones.tipClonar")}</Text>
              </Card>
            </div>
          </Sider>
        )}

        {shellEstrecho && (
          <Drawer
            placement="right"
            open={panelAbierto}
            onClose={() => setPanelAbierto(false)}
            size={320}
            title={t("evaluaciones.titulo")}
            styles={{ body: { padding: 12 } }}
          >
            <div className="revi-page-evaluaciones__sider-inner">
              <Card bordered>
                <Space
                  orientation="vertical"
                  style={{ width: "100%" }}
                  size={10}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    block
                    onClick={openCreate}
                  >
                    {t("evaluaciones.acciones.crear")}
                  </Button>

                  <Button icon={<TeamOutlined />} block onClick={openShared}>
                    {t("evaluaciones.acciones.compartidas")}
                  </Button>

                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("evaluaciones.leyendaVisibilidad")}
                  </Text>
                </Space>
              </Card>

              <Divider />

              <Card bordered>
                <Text type="secondary">{t("evaluaciones.tipClonar")}</Text>
              </Card>
            </div>
          </Drawer>
        )}

        <EvaluationDetailModal
          isOpen={detailOpen}
          onClose={closeDetail}
          evaluation={selected}
          loading={detailLoading}
        />

        {wizardState?.open ? (
          <EvaluationWizard
            key={wizardState.data ? `edit-${wizardState.data._id}` : "create"}
            isOpen={true}
            initialData={wizardState.data}
            onClose={closeWizard}
            onSuccess={onWizardSuccess}
          />
        ) : null}

        <SharedEvaluationsModal
          isOpen={sharedOpen}
          onClose={closeShared}
          loading={sharedLoading}
          evaluations={sharedEvals}
          onView={openDetail}
          onClone={onClone}
        />

        <QuickEvaluationModal
          isOpen={quickEvalState.open}
          onClose={closeQuickEval}
          evaluation={quickEvalState.evaluation}
          accessToken={accessToken}
          messageApi={messageApi}
        />
      </Layout>
    </ConfigProvider>
  );
}

export function Page_Therapist_Evaluaciones() {
  return (
    <AntApp>
      <PageTherapistEvaluacionesInner />
    </AntApp>
  );
}
